import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { analyticsApi } from '../api/client';
import clsx from 'clsx';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

// TypeScript interfaces for proper typing
interface StorePerformance {
  store_id: string;
  store_name: string;
  order_count: number;
  refund_count: number;
  refund_rate: number;
  dispute_count: number;
  win_rate: number;
  recovered: number;
}

interface TopRefundedItem {
  item_name: string;
  claim_count: number;
  total_value: number;
  avg_refund: number;
  common_reason?: string;
}

interface DateRange {
  value: string;
  label: string;
  days: number;
}

const DATE_RANGES: DateRange[] = [
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
];

export default function Analytics() {
  const [stores, setStores] = useState<StorePerformance[]>([]);
  const [topItems, setTopItems] = useState<TopRefundedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState('');
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    fetchData();
  }, [selectedStore, dateRange]);

  async function fetchData() {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { days: parseInt(dateRange) };
      if (selectedStore) params.store_id = selectedStore;

      const [storesRes, itemsRes] = await Promise.all([
        analyticsApi.stores(params),
        analyticsApi.topItems({ ...params, limit: 10 }),
      ]);

      setStores(storesRes.data.data || []);
      setTopItems(itemsRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // Calculate summary metrics
  const totalOrders = stores.reduce((sum, s) => sum + s.order_count, 0);
  const totalRecovered = stores.reduce((sum, s) => sum + s.recovered, 0);
  const avgWinRate = stores.length
    ? Math.round(stores.reduce((sum, s) => sum + s.win_rate, 0) / stores.length)
    : 0;
  const highRiskStores = stores.filter((s) => s.refund_rate > 5).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
          <p className="text-slate-500 mt-1">Deep insights into refund patterns and recovery performance</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="">All Stores</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>
                {s.store_name}
              </option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            {DATE_RANGES.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Orders"
          value={totalOrders.toLocaleString()}
          icon={ChartBarIcon}
          color="blue"
        />
        <SummaryCard
          title="Total Recovered"
          value={formatCurrency(totalRecovered)}
          icon={ArrowTrendingUpIcon}
          color="emerald"
          trend={{ value: 12, positive: true }}
        />
        <SummaryCard
          title="Avg Win Rate"
          value={`${avgWinRate}%`}
          icon={ChartBarIcon}
          color="indigo"
        />
        <SummaryCard
          title="High Risk Stores"
          value={highRiskStores.toString()}
          icon={ExclamationTriangleIcon}
          color={highRiskStores > 0 ? 'red' : 'slate'}
          alert={highRiskStores > 0}
        />
      </div>

      {/* Store Leaderboard */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Store Performance</h2>
            <p className="text-sm text-slate-500 mt-0.5">Ranked by recovery efficiency</p>
          </div>
          <BuildingStorefrontIcon className="w-5 h-5 text-slate-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Orders
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Refund Rate
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Disputes
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Win Rate
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Recovered
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stores.map((store, i) => (
                <tr
                  key={store.store_id}
                  className={clsx(
                    'transition-colors',
                    store.refund_rate > 5 ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'
                  )}
                >
                  <td className="px-6 py-4">
                    <span
                      className={clsx(
                        'inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold',
                        i === 0 && 'bg-amber-100 text-amber-700',
                        i === 1 && 'bg-slate-200 text-slate-700',
                        i === 2 && 'bg-orange-100 text-orange-700',
                        i > 2 && 'bg-slate-100 text-slate-600'
                      )}
                    >
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{store.store_name}</span>
                      {store.refund_rate > 5 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          High Risk
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">{store.order_count.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={clsx(
                        'text-sm font-medium',
                        store.refund_rate > 5 ? 'text-red-600' : 'text-slate-900'
                      )}
                    >
                      {store.refund_rate}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">{store.dispute_count}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full"
                          style={{ width: `${store.win_rate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-900">{store.win_rate}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-emerald-600">
                      {formatCurrency(store.recovered)}
                    </span>
                  </td>
                </tr>
              ))}
              {stores.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                        <BuildingStorefrontIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-500">No store data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Refunded Items Chart */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Top Refunded Items</h2>
            <p className="text-sm text-slate-500 mt-0.5">Items with highest refund value</p>
          </div>
          <div className="p-6">
            {topItems.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topItems} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} stroke="#94a3b8" fontSize={12} />
                    <YAxis
                      dataKey="item_name"
                      type="category"
                      width={120}
                      stroke="#94a3b8"
                      fontSize={12}
                      tick={{ fill: '#64748b' }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="total_value" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <ChartBarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No item data available</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actionable Insights */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Actionable Insights</h2>
              <p className="text-sm text-slate-500 mt-0.5">Recommendations based on data patterns</p>
            </div>
            <LightBulbIcon className="w-5 h-5 text-amber-500" />
          </div>
          <div className="p-6 space-y-4">
            {topItems.slice(0, 5).map((item, i) => (
              <InsightCard key={i} item={item} formatCurrency={formatCurrency} />
            ))}
            {topItems.length === 0 && (
              <div className="text-center py-8">
                <LightBulbIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No insights available yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'emerald' | 'indigo' | 'red' | 'slate';
  trend?: { value: number; positive: boolean };
  alert?: boolean;
}

function SummaryCard({ title, value, icon: Icon, color, trend, alert }: SummaryCardProps) {
  const colors = {
    blue: 'bg-gradient-to-br from-amber-500 to-amber-600',
    emerald: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    indigo: 'bg-gradient-to-br from-orange-500 to-orange-600',
    red: 'bg-gradient-to-br from-red-500 to-red-600',
    slate: 'bg-gradient-to-br from-slate-500 to-slate-600',
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow',
        alert ? 'border-red-200' : 'border-slate-200'
      )}
    >
      <div className="flex items-start justify-between">
        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', colors[color])}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <div
            className={clsx(
              'flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium',
              trend.positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            )}
          >
            {trend.positive ? (
              <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
            )}
            {trend.value}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
      </div>
    </div>
  );
}

interface InsightCardProps {
  item: TopRefundedItem;
  formatCurrency: (value: number) => string;
}

function InsightCard({ item, formatCurrency }: InsightCardProps) {
  return (
    <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-slate-900">{item.item_name}</span>
        <span className="inline-flex items-center px-2 py-0.5 bg-white rounded-lg text-xs font-medium text-slate-600">
          {item.claim_count} claims
        </span>
      </div>
      <p className="text-sm text-slate-600">
        <span className="font-medium text-red-600">{formatCurrency(item.total_value)}</span> in refunds.
        {item.common_reason ? (
          <span> Most common reason: {item.common_reason}.</span>
        ) : (
          <span> Consider reviewing packing procedures for this item.</span>
        )}
      </p>
    </div>
  );
}
